import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Contact } from '@/types/contact';

/**
 * NOT-579 — the contact-detail query is deliberately `gcTime: 0` (live state),
 * so it evicts the moment you navigate away to create an estimate/job. That
 * forces the create form to refetch the contact and the customer section sits
 * empty for a couple of seconds.
 *
 * Seed the already-loaded contact into a dedicated, short-lived cache entry so
 * the follow-on estimate/job form can hydrate its customer section
 * synchronously (from data the app already had) instead of refetching. A cold
 * deep-link — no contact page visited — finds nothing here and falls back to a
 * fetch with a skeleton.
 */
const contactPrefillKey = (contactId: string) => ['contact-prefill', contactId] as const;

/** Cache the loaded contact for a follow-on create form (call on the contact page). */
export function useSeedContactPrefill(contact: Contact | undefined) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!contact?.id) return;
    const key = contactPrefillKey(String(contact.id));
    // The contact page loads soft-deleted contacts (includeDeleted) to allow
    // restore. Never prefill an archived customer — and drop any stale snapshot
    // seeded before it was archived — so create forms fall back to the direct
    // fetch, which excludes archived contacts. NOT-579 review.
    if (contact.isDeleted) {
      queryClient.removeQueries({ queryKey: key });
      return;
    }
    queryClient.setQueryData(key, contact);
  }, [queryClient, contact]);
}

/** Read the seeded contact synchronously (returns undefined on a cold deep-link). */
export function useContactPrefill(contactId: string | undefined): Contact | undefined {
  const queryClient = useQueryClient();
  if (!contactId) return undefined;
  return queryClient.getQueryData<Contact>(contactPrefillKey(contactId));
}
